/**
 * LE TRACÉ D'UN ÉLÈVE, CHEZ SON TUTEUR — atelier de figures, lot L7 (AB#943 ;
 * `docs/pilotage/atelier-figures/CONCEPTION.md` § 4). Deux pièces, posées sous
 * « Ce que {prénom} a tracé » dans « Travaux et corrections » (`TravauxEleve`) :
 *
 *  - `ConstructionRejoueeWeb` — LECTURE SEULE : le tuteur fait défiler les
 *    étapes de la construction de l'élève et voit OÙ elle a dévié ; il lit
 *    aussi la relecture « On a lu… » que l'élève a confirmée. Calculé dans la
 *    page, à l'ouverture, par les fonctions partagées : aucun appel ;
 *  - `NotesDuTuteurSurLeTrace` — L'ANNOTATION, option B (décision 3 du
 *    fondateur) : le tuteur touche un objet du tracé, épingle peut-être un
 *    constat, écrit UNE phrase. Aucun dessin ; le verdict n'est jamais
 *    modifié. Lue et écrite à la demande, à l'ouverture d'un `<details>` :
 *    vingt travaux par page ne font pas vingt lectures.
 *
 * Le texte d'une note est une DONNÉE : il s'affiche en texte brut (React
 * l'échappe), jamais par `TexteMaths`.
 */
import {
  MAX_CARACTERES_NOTE_TUTEUR,
  type NoteDuTuteurConsoleDto,
  type NotesDuTravailResponse,
  type TraceDeTravailDto,
} from '@silex/shared';
import { useId, useMemo, useState, type ReactNode } from 'react';

import { ApiError, api } from '../../api/client';
import { ToileDeTrace } from '../../components/FigureV2Web';
import { planDEnonce, svgDesTraces } from '../../lib/figures-v2-web';
import { echelleDuTrace, versFigure } from '../../lib/relecture-trace';
import {
  direLEtape,
  objetSousLeClic,
  objetsATracer,
  rejeuDuTrace,
  type RejeuDuTrace,
} from '../../lib/trace-du-tuteur';
import { formatDate } from '../../lib/tuteur-format';

import './trace-du-tuteur.css';

/* ─────────────────────────── la construction rejouée ─────────────────────────── */

export function Rejeu({
  r,
  trace,
  figureDEnonce,
  prenom,
}: {
  readonly r: RejeuDuTrace;
  readonly trace: TraceDeTravailDto;
  readonly figureDEnonce: unknown;
  readonly prenom: string;
}): ReactNode {
  const etapes = r.construction.etapes;
  const [k, setK] = useState(r.deviation ?? etapes.length);
  const etape = etapes[Math.min(Math.max(k, 1), etapes.length) - 1];
  const svg = useMemo(
    () =>
      etape === undefined
        ? ''
        : svgDesTraces({ eleve: etape.document, attendu: trace.attendu }, figureDEnonce).svg,
    [etape, trace.attendu, figureDEnonce],
  );
  if (etape === undefined) return <p className="note-line">Aucune étape à rejouer.</p>;
  const ici = etape.rang === r.deviation;
  return (
    <div className="tt-rejeu" data-testid="manuel-travail-rejeu">
      <p className="note-line" data-testid="manuel-travail-geste-lu">
        <span className="lbl">Relu et confirmé par {prenom}</span> {r.gesteLu}
      </p>
      <p className="cell-sub">
        {r.construction.avecHistorique
          ? `Rejouée commande par commande, dans l’ordre où ${prenom} a construit.`
          : `Cet essai ne garde pas l’ordre des commandes : rejouée objet par objet, dans l’ordre de création.`}
      </p>
      <ToileDeTrace svg={svg} testid="manuel-travail-rejeu-toile" />
      <div className="tr-pas" role="group" aria-label="Les étapes de la construction">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setK(1)}
          disabled={etape.rang === 1}
        >
          Début
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setK(etape.rang - 1)}
          disabled={etape.rang === 1}
          data-testid="manuel-travail-rejeu-precedente"
        >
          ← Étape
        </button>
        <span className="tr-pas__compte" data-testid="manuel-travail-rejeu-etape" aria-live="polite">
          {direLEtape(r, etape.rang)}
        </span>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setK(etape.rang + 1)}
          disabled={etape.rang === etapes.length}
          data-testid="manuel-travail-rejeu-suivante"
        >
          Étape →
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setK(etapes.length)}
          disabled={etape.rang === etapes.length}
        >
          Fin
        </button>
        {r.deviation !== null ? (
          <button
            type="button"
            className="btn btn--quiet btn--sm"
            onClick={() => setK(r.deviation ?? 1)}
            disabled={ici}
            data-testid="manuel-travail-rejeu-deviation"
          >
            Aller à la déviation
          </button>
        ) : null}
      </div>
      <p
        className={ici ? 'tt-etape tt-etape--deviation' : 'tt-etape'}
        data-testid="manuel-travail-rejeu-phrase"
      >
        {etape.phrase}
      </p>
      {ici && r.constatFautif !== null ? (
        <p className="cell-sub" data-testid="manuel-travail-rejeu-pourquoi">
          Après cette étape, l’objet ne change plus. Le vérificateur a relevé : « {r.constatFautif} »
        </p>
      ) : null}
      {r.deviation === null && trace.constats.some((c) => c.etat !== 'tenue') ? (
        <p className="cell-sub">
          Aucune étape ne se désigne : la contrainte manque faute d’objet, ou l’essai est d’avant le
          relevé de l’objet en cause.
        </p>
      ) : null}
      <p className="note-line">Plein : la construction à cette étape. Pointillé : le tracé attendu.</p>
    </div>
  );
}

/** « Rejouer la construction » : calculée à l'ouverture, lecture seule. */
export function ConstructionRejoueeWeb({
  trace,
  figureDEnonce,
  prenom,
}: {
  readonly trace: TraceDeTravailDto;
  readonly figureDEnonce: unknown;
  readonly prenom: string;
}): ReactNode {
  const [ouvert, setOuvert] = useState(false);
  const r = useMemo(
    () => (ouvert ? rejeuDuTrace(trace, figureDEnonce) : null),
    [ouvert, trace, figureDEnonce],
  );
  return (
    <details
      className="sequence-raison"
      data-testid="manuel-travail-construction"
      onToggle={(e) => {
        if (e.currentTarget.open) setOuvert(true);
      }}
    >
      <summary data-testid="manuel-travail-construction-voir">
        Rejouer la construction de {prenom}
      </summary>
      {r !== null ? (
        <Rejeu r={r} trace={trace} figureDEnonce={figureDEnonce} prenom={prenom} />
      ) : null}
    </details>
  );
}

/* ─────────────────────────── la note du tuteur ─────────────────────────── */

const RAYON_AIMANT_PX = 14;

type EtatNotes =
  | { readonly phase: 'repos' | 'lecture' | 'erreur' }
  | { readonly phase: 'lues'; readonly lues: NotesDuTravailResponse };

function UneNote({
  note,
  onRetirer,
}: {
  readonly note: NoteDuTuteurConsoleDto;
  readonly onRetirer: (() => void) | null;
}): ReactNode {
  return (
    <li className="tt-note" data-testid="manuel-travail-note">
      <div className="tt-note__ancre">
        Sur {note.ancre.libelle}
        <span className="cell-sub"> · {formatDate(note.creeLe)}</span>
      </div>
      {/* UNE DONNÉE : affichée telle quelle, jamais interprétée. */}
      <div className="tt-note__texte">{note.texte}</div>
      {note.constat !== null ? (
        <div className="cell-sub">Constat épinglé : « {note.constat.texte} »</div>
      ) : null}
      {onRetirer !== null ? (
        <button
          type="button"
          className="btn btn--quiet btn--sm"
          onClick={onRetirer}
          data-testid="manuel-travail-note-retirer"
        >
          Retirer ma note
        </button>
      ) : null}
    </li>
  );
}

/**
 * « Annoter ce tracé » : les notes déjà écrites, et le geste d'en écrire une.
 * L'élève les lit, à la relecture de son essai, sous « Ton tuteur a ajouté ».
 */
export function NotesDuTuteurSurLeTrace({
  eleveId,
  copieId,
  trace,
  figureDEnonce,
  prenom,
}: {
  readonly eleveId: string;
  readonly copieId: string;
  readonly trace: TraceDeTravailDto;
  readonly figureDEnonce: unknown;
  readonly prenom: string;
}): ReactNode {
  const id = useId();
  const [etat, setEtat] = useState<EtatNotes>({ phase: 'repos' });
  const [objet, setObjet] = useState<string | null>(null);
  const [constat, setConstat] = useState<number | null>(null);
  const [texte, setTexte] = useState('');
  const [envoi, setEnvoi] = useState<'repos' | 'envoi' | { readonly erreur: string }>('repos');
  const objets = useMemo(() => objetsATracer(trace, figureDEnonce), [trace, figureDEnonce]);
  const dessin = useMemo(() => svgDesTraces(trace, figureDEnonce).svg, [trace, figureDEnonce]);
  const echelle = useMemo(
    () =>
      echelleDuTrace({
        repere: trace.eleve.repere,
        enonce: planDEnonce(figureDEnonce),
      }),
    [trace, figureDEnonce],
  );

  const lire = (): void => {
    if (etat.phase === 'lecture' || etat.phase === 'lues') return;
    setEtat({ phase: 'lecture' });
    api
      .manuelNotesDuTravail(eleveId, copieId)
      .then((lues) => setEtat({ phase: 'lues', lues }))
      .catch(() => setEtat({ phase: 'erreur' }));
  };
  const ecrire = (): void => {
    if (objet === null || etat.phase !== 'lues') return;
    setEnvoi('envoi');
    api
      .ecrireNoteDuTuteur(eleveId, copieId, {
        texte,
        objet,
        ...(constat !== null ? { constat } : {}),
      })
      .then(({ note }) => {
        setEtat({ phase: 'lues', lues: { ...etat.lues, notes: [...etat.lues.notes, note] } });
        setTexte('');
        setConstat(null);
        setEnvoi('repos');
      })
      .catch((e: unknown) =>
        setEnvoi({
          erreur:
            e instanceof ApiError
              ? e.message
              : 'La note ne s’est pas écrite. Réessaie dans un moment.',
        }),
      );
  };
  const retirer = (noteId: string): void => {
    if (etat.phase !== 'lues') return;
    api
      .retirerNoteDuTuteur(eleveId, copieId, noteId)
      .then(() =>
        setEtat({
          phase: 'lues',
          lues: { ...etat.lues, notes: etat.lues.notes.filter((n) => n.id !== noteId) },
        }),
      )
      .catch(() => setEnvoi({ erreur: 'La note ne s’est pas retirée. Réessaie dans un moment.' }));
  };

  const choisi = objets.find((o) => o.id === objet) ?? null;
  const restant = MAX_CARACTERES_NOTE_TUTEUR - [...texte].length;

  return (
    <details
      className="sequence-raison"
      data-testid="manuel-travail-notes"
      onToggle={(e) => {
        if (e.currentTarget.open) lire();
      }}
    >
      <summary data-testid="manuel-travail-notes-voir">Annoter ce tracé pour {prenom}</summary>
      {etat.phase !== 'lues' ? (
        <p className="cell-sub" role={etat.phase === 'erreur' ? 'alert' : undefined}>
          {etat.phase === 'erreur'
            ? 'Les notes ne se lisent pas pour l’instant. Referme, puis rouvre pour réessayer.'
            : 'Lecture des notes…'}
        </p>
      ) : (
        <div className="tt-notes">
          <p className="cell-sub">
            {prenom} lira chaque note à la relecture de cet essai, sous « Ton tuteur a ajouté ».
            Une note est une phrase sur un objet du tracé : elle ne change pas le verdict.
          </p>
          {etat.lues.notes.length === 0 ? (
            <p className="note-line" data-testid="manuel-travail-notes-vide">
              Aucune note sur cet essai.
            </p>
          ) : (
            <ul className="tt-notes__liste">
              {etat.lues.notes.map((n) => (
                <UneNote
                  key={n.id}
                  note={n}
                  onRetirer={n.deMoi && etat.lues.peutEcrire ? () => retirer(n.id) : null}
                />
              ))}
            </ul>
          )}

          {!etat.lues.peutEcrire ? (
            <p className="cell-sub">Seul le tuteur de {prenom} écrit une note.</p>
          ) : etat.lues.notes.length >= etat.lues.maximum ? (
            <p className="cell-sub">
              {etat.lues.maximum} notes au plus sur un essai : le reste se dit en séance.
            </p>
          ) : objets.length === 0 ? (
            <p className="cell-sub">Ce tracé n’a pas d’objet à annoter.</p>
          ) : (
            <div className="tt-ecrire" data-testid="manuel-travail-note-formulaire">
              <h5 className="tt-ecrire__titre">1. Touche un objet du tracé</h5>
              <ToileDeTrace
                svg={dessin}
                testid="manuel-travail-note-toile"
                onToucher={(aff, pixelsParUnite) => {
                  const touche = objetSousLeClic(
                    trace,
                    figureDEnonce,
                    versFigure(echelle, aff),
                    RAYON_AIMANT_PX / pixelsParUnite / echelle.kx,
                  );
                  if (touche !== null) setObjet(touche);
                }}
              />
              <div className="tr-outils" role="group" aria-label="Les objets du tracé">
                {objets.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    className={o.id === objet ? 'btn btn--sm' : 'btn btn--ghost btn--sm'}
                    aria-pressed={o.id === objet}
                    onClick={() => setObjet(o.id)}
                    data-testid={`manuel-travail-note-objet-${o.id}`}
                  >
                    {o.libelle}
                  </button>
                ))}
              </div>
              <p className="cell-sub" data-testid="manuel-travail-note-objet">
                {choisi === null ? 'Aucun objet touché.' : `Objet touché : ${choisi.libelle}.`}
              </p>
              {trace.constats.length > 0 ? (
                <div className="field">
                  <label htmlFor={`${id}-constat`}>2. Épingler un constat (facultatif)</label>
                  <select
                    id={`${id}-constat`}
                    value={constat === null ? '' : String(constat)}
                    onChange={(e) =>
                      setConstat(e.target.value === '' ? null : Number(e.target.value))
                    }
                    data-testid="manuel-travail-note-constat"
                  >
                    <option value="">Aucun</option>
                    {trace.constats.map((c, i) => (
                      <option key={i} value={i}>
                        {c.texte}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div className="field">
                <label htmlFor={`${id}-texte`}>3. Une phrase pour {prenom}</label>
                <textarea
                  id={`${id}-texte`}
                  rows={2}
                  maxLength={MAX_CARACTERES_NOTE_TUTEUR}
                  value={texte}
                  onChange={(e) => setTexte(e.target.value)}
                  placeholder="Regarde le sens de ta rotation."
                  data-testid="manuel-travail-note-texte"
                />
                <span className="cell-sub">
                  {restant} caractère{restant > 1 ? 's' : ''} restant{restant > 1 ? 's' : ''}
                </span>
              </div>
              <div className="tr-pas">
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={objet === null || texte.trim() === '' || envoi === 'envoi'}
                  onClick={ecrire}
                  data-testid="manuel-travail-note-ajouter"
                >
                  {envoi === 'envoi' ? 'Envoi…' : 'Ajouter la note'}
                </button>
                {typeof envoi === 'object' ? (
                  <span className="cell-sub" role="alert">
                    {envoi.erreur}
                  </span>
                ) : null}
              </div>
            </div>
          )}
        </div>
      )}
    </details>
  );
}
